import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { PlaceBetResponse, PlaceBetStatus } from '@frontend/sports/types/betslip';
import { BalancePropertiesService } from '@frontend/vanilla/features/balance-properties';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, catchError, concatMap, delay, exhaustMap, from, mergeMap, of, startWith, switchMap } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { OverAskRejectReason } from '../../../model/over-ask/over-ask';
import { BetslipGeneratorInputBuilder } from '../../bet-generation/services/betslip-generator-input-builder.service';
import { BetslipGeneratorService } from '../../bet-generation/services/betslip-generator.service';
import { BetslipPlaceBetActions, PlaceBetFailedReason, QueryPlaceStatusPayload } from '../../betplacement/actions';
import { IPlacedBetslipResult } from '../../betplacement/models';
import PlaceBetResponseProcessorService from '../../betplacement/services/place-bet-response-processor.service';
import PlaceBetService from '../../betplacement/services/place-bet.service';
import PlaceBetRequestBuilder from '../../betplacement/services/request/place-bet-request-builder';
import { BetslipResultActions } from '../../result/actions';
import { RewardTokensActions } from '../../reward-tokens/actions';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { TechnicalError } from '../../validation/errors/general/technical-error';
import { NotLoggedInError } from '../../validation/errors/user/not-logged-in-error';
import BetValidationService from '../../validation/services/composition/bets/bet-validation.service';
import { emptyPickErrors } from '../../validation/state';
import { BetslipOverAskActions } from '../actions';
import { OverAskTrackingActions } from '../tracking.actions';

/**
 * Basically this effect is a redundant in betstation context,
 * for now we keep it in order to handle both modern and legacy context.
 * Remove it once we migrate to NgRx state.
 */
@Injectable()
export class OverAskPlaceEffects {
    private logger: SportsRemoteLogger;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private generatorInputBuilder: BetslipGeneratorInputBuilder,
        private betValidation: BetValidationService,
        private betslipGenerator: BetslipGeneratorService,
        private placeBetRequestBuilder: PlaceBetRequestBuilder,
        private placeBetApi: PlaceBetService,
        private placeBetResponseProcessorService: PlaceBetResponseProcessorService,
        loggerFactory: LoggerFactory,
        private balanceService: BalancePropertiesService,
    ) {
        this.logger = loggerFactory.getLogger('OverAskPlaceEffects');
    }

    handleQueryStatusEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.getPlaceBetStatus),
            switchMap((action) => {
                return this.placeBetApi.queryStatus(action.request.requestId).pipe(
                    concatMap((response) => {
                        const context: QueryPlaceStatusPayload = {
                            request: action.request,
                            requestPicks: action.requestPicks,
                            state: action.state,
                            trackBetContext: action.trackBetContext,
                            trackBetContextV2: action.trackBetContextV2,
                            slips: action.slips,
                        };

                        return this.processPlaceBetPlacementResponse(response.body, context);
                    }),
                    catchError((error: HttpErrorResponse) => this.processException(error)),
                );
            }),
        );
    });

    handleOverAskPlaceEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.placeBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            exhaustMap(([action, betslipState]) => {
                try {
                    const requestId = betslipState.overAskState.requestId;
                    const overAskBets = betslipState.overAskState.bets;
                    const input = this.generatorInputBuilder.getOverAskInput(overAskBets, betslipState.rewardTokens.tokens);
                    const restrictions = this.betValidation.getRestrictions(betslipState.picks.pickList, false, false);
                    const overrides = this.generatorInputBuilder.createOverAskCalculatorOverrides(overAskBets);
                    const output = this.betslipGenerator.calculate(input, { restrictions, overrides });

                    const {
                        request: betPlacementRequest,
                        requestPicks,
                        trackBetContext,
                        trackBetContextV2,
                        slips,
                    } = this.placeBetRequestBuilder.buildFromSlips(betslipState, requestId, output.slips);

                    betPlacementRequest.overAskOfferId = betslipState.overAskState.offerId;

                    return this.placeBetApi.placeBet(betPlacementRequest, trackBetContext, trackBetContextV2).pipe(
                        mergeMap((response) => {
                            const placeBetStatusPayload: QueryPlaceStatusPayload = {
                                request: betPlacementRequest,
                                requestPicks,
                                state: betslipState,
                                trackBetContext,
                                trackBetContextV2,
                                slips,
                            };
                            this.balanceService.refresh();

                            return this.processPlaceBetPlacementResponse(response.body, placeBetStatusPayload);
                        }),
                        catchError((error: Error) => this.processException(error)),
                        startWith(BetslipActions.setState({ state: BetslipState.Placing }), BetslipPlaceBetActions.startedPlaceBet()),
                    );
                } catch (error) {
                    this.logger.error(error);

                    return this.processException(error);
                }
            }),
        );
    });

    private processPlaceBetPlacementResponse(response: PlaceBetResponse | null, placeBetStatusPayload: QueryPlaceStatusPayload): Observable<Action> {
        if (!response) {
            this.logger.error({}, 'Place bet failed with technical error');

            return this.processException(new TechnicalError());
        }
        const status = this.placeBetResponseProcessorService.getStatus(response);
        if (status === PlaceBetStatus.Pending) {
            const pendingResult = this.placeBetResponseProcessorService.processPending(response);

            return this.processPlaceBetPendingResponse(pendingResult, placeBetStatusPayload);
        } else {
            const result = this.placeBetResponseProcessorService.processDone(response, placeBetStatusPayload);

            return this.processDonePlacement(result, placeBetStatusPayload, response);
        }
    }

    private processPlaceBetPendingResponse(
        pendingResult: {
            delay: number;
        },
        payload: QueryPlaceStatusPayload,
    ): Observable<Action> {
        return of(BetslipOverAskActions.getPlaceBetStatus(payload)).pipe(delay(pendingResult.delay));
    }

    private processException(error: Error | TechnicalError | unknown): Observable<Action> {
        const betPlacementErrors: BetslipError[] = [];

        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            betPlacementErrors.push(new NotLoggedInError());
        } else {
            betPlacementErrors.push(new TechnicalError());
        }

        return from([
            BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.TechnicalError }),
            BetslipErrorsActions.placeBetResponseErrors({
                pickErrors: emptyPickErrors(),
                betslipErrors: betPlacementErrors,
                slipErrors: {},
            }),
            BetslipActions.setState({ state: BetslipState.Edit }),
            BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.PlaceBetFailed }),
        ]);
    }

    private processDonePlacement(
        placeBetResult: IPlacedBetslipResult,
        payload: QueryPlaceStatusPayload,
        response: PlaceBetResponse | null,
    ): Observable<Action> {
        const isSuccess = placeBetResult.status === PlaceBetStatus.Success;

        return new Observable<Action>((subscriber) => {
            if (isSuccess) {
                BetslipActions.setState({ state: BetslipState.Result });
            } else {
                subscriber.next(BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.PlaceBetFailed }));
            }
            subscriber.next(BetslipPlaceBetActions.trackBetPlacementResult({ placeBetResult, ...payload }));
            subscriber.next(BetslipPlaceBetActions.finishPlaceBet({ placeBetResult, ...payload }));
            subscriber.next(
                BetslipResultActions.decisionSource({
                    decisionSource: payload.state.overAskState.decisionSource,
                    isOfferModified: payload.state.overAskState.isofferModified,
                }),
            );
            subscriber.next(OverAskTrackingActions.trackOddsChange({ overAskResponse: response }));
            subscriber.next(BetslipOverAskActions.finishFlow());
            subscriber.next(RewardTokensActions.refresh());

            subscriber.complete();
        });
    }
}
